// Kopf der Ticket-Liste (Handoff §1): Titel, Liste|Kanban, Neu laden, Export-Menü, Neues Ticket.
import { useState } from 'react';
import {
  ArrowsClockwise, CaretDown, ChartBar, Export, FileCsv, FilePdf, FlowArrow, Kanban, ListBullets, MicrosoftExcelLogo, Plus, SpinnerGap, type Icon,
} from '@phosphor-icons/react';
import { ImmoButton, MenueTitel, Popover } from '../../../../components/immobilien/ui';
import { Segment } from '../../../../components/ui/Segment';
import type { ExportColumn, ExportStats } from '../../../../lib/export/types';
import { exportCsv } from '../../../../lib/export/exportCsv';
import { exportXlsx } from '../../../../lib/export/exportXlsx';
import { exportPdfReport, exportPdfTable } from '../../../../lib/export/exportPdf';
import { RundKnopf } from './teile';

export type ListenAnsicht = 'liste' | 'kanban' | 'vorgaenge';

type Format = 'csv' | 'xlsx' | 'pdf-table' | 'pdf-report';
const FORMATE: readonly { key: Format; titel: string; icon: Icon; farbe: string }[] = [
  { key: 'csv', titel: 'CSV', icon: FileCsv, farbe: 'var(--ok)' },
  { key: 'xlsx', titel: 'Excel', icon: MicrosoftExcelLogo, farbe: 'var(--ok)' },
  { key: 'pdf-table', titel: 'PDF Tabelle', icon: FilePdf, farbe: 'var(--bad)' },
  { key: 'pdf-report', titel: 'PDF Bericht', icon: ChartBar, farbe: 'var(--info)' },
];

export interface ListenKopfProps<T> {
  mobil: boolean;
  ansicht: ListenAnsicht;
  setAnsicht: (a: ListenAnsicht) => void;
  /** Kanban nur auf breiten Schirmen (wie bisher: Desktop) */
  kanbanMoeglich: boolean;
  onNeuLaden: () => void;
  laedt: boolean;
  onNeuesTicket: () => void;
  export: { titel: string; spalten: ExportColumn[]; ansicht: readonly T[]; alle: () => Promise<readonly T[]>; statistik: ExportStats };
  onToast: (text: string, fehler?: boolean) => void;
}

export function ListenKopf<T>({ mobil, ansicht, setAnsicht, kanbanMoeglich, onNeuLaden, laedt, onNeuesTicket, export: ex, onToast }: ListenKopfProps<T>) {
  const [exportLaeuft, setExportLaeuft] = useState(false);

  const exportiere = async (f: (typeof FORMATE)[number], umfang: 'ansicht' | 'alle', schliessen: () => void) => {
    setExportLaeuft(true);
    try {
      const daten = umfang === 'alle' ? await ex.alle() : ex.ansicht;
      const config = { title: ex.titel, columns: ex.spalten, data: [...daten], stats: ex.statistik };
      if (f.key === 'csv') exportCsv(config);
      else if (f.key === 'xlsx') exportXlsx(config);
      else if (f.key === 'pdf-table') exportPdfTable(config);
      else exportPdfReport(config);
      schliessen();
      onToast(`Export ${f.titel} (${umfang === 'alle' ? 'Alle Daten' : 'Aktuelle Ansicht'}) erstellt`);
    } catch (e: unknown) {
      onToast(`Export fehlgeschlagen: ${e instanceof Error ? e.message : 'Unbekannter Fehler'}`, true);
    } finally {
      setExportLaeuft(false);
    }
  };

  return (
    <header className="flex flex-wrap items-end justify-between" style={{ gap: 14 }}>
      <div className="flex min-w-0 flex-col" style={{ gap: 4 }}>
        <h1 className="m-0" style={{ fontSize: mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05 }}>Tickets</h1>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink2)' }}>Aufgaben &amp; Anfragen</span>
      </div>
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <Segment ariaLabel="Ansicht" art="akzent" hoehe={34} wert={ansicht} onChange={setAnsicht} eintraege={[
          { key: 'liste', label: 'Liste', icon: ListBullets, tip: 'Liste' },
          ...(kanbanMoeglich ? [{ key: 'kanban' as const, label: 'Kanban', icon: Kanban, tip: 'Kanban' }] : []),
          { key: 'vorgaenge', label: 'Vorgänge', icon: FlowArrow, tip: 'Vorgänge – Offene Vorgänge (Kündigung, Schaden, Zählerstand …) mit fehlenden Angaben' },
        ]} />
        <RundKnopf icon={ArrowsClockwise} label="Aktualisieren" onClick={onNeuLaden} dreht={laedt} />
        <Popover label="Export" breite={240} ausloeser={({ offen, umschalten, ref }) => (
          <ImmoButton ref={ref} art="glas" groesse={44} pille icon={exportLaeuft ? SpinnerGap : Export} onClick={umschalten} disabled={exportLaeuft}
            aria-expanded={offen} aria-haspopup="menu" data-tip="Export – Die aktuell gefilterte Ticketliste herunterladen">
            Export<CaretDown size={11} weight="bold" color="var(--ink3)" />
          </ImmoButton>
        )}>
          {schliessen => (
            <div role="menu" className="flex flex-col" style={{ padding: 4 }}>
              {(['ansicht', 'alle'] as const).map(umfang => (
                <div key={umfang} className="flex flex-col">
                  <MenueTitel>{umfang === 'ansicht' ? `Aktuelle Ansicht (${ex.ansicht.length})` : 'Alle Daten'}</MenueTitel>
                  {FORMATE.map(f => {
                    const I = f.icon;
                    return (
                      <button key={f.key} type="button" role="menuitem" disabled={exportLaeuft} onClick={() => void exportiere(f, umfang, schliessen)}
                        data-tip={`Export als ${f.titel} – Datei mit den gefilterten Tickets erzeugen`}
                        className="immo-menue-zeile flex items-center text-left"
                        style={{ gap: 10, height: 40, padding: '0 10px', borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink)',
                          fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                        <I size={18} weight="fill" color={f.farbe} aria-hidden />{f.titel}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </Popover>
        <ImmoButton art="akzent" groesse={44} icon={Plus} onClick={onNeuesTicket} data-tip="Neues Ticket – Schadensmeldung oder Aufgabe anlegen">
          Neues Ticket
        </ImmoButton>
      </div>
    </header>
  );
}
